import moment from 'moment/min/moment-with-locales';
import { Trans, useTranslation } from 'react-i18next';
import { prettyDate, prettyFloatMoney } from '@/helpers/utils';
import { DATE_FORMAT_MMMM_YYYY } from '@/helpers/common';
import { safeDecimal } from '@scramble/utils';
import { useModal } from '@/providers/modal';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useGetFundraisingDates } from '@/query/queries/investing/useGetFundraisingDates';
import { useGetBonusGroup } from '@/query/queries/marketing/useGetBonusGroup';
import { useWindowSize } from '@/hooks/useWindowSize';
import { Button } from '@scramble/ui';
import s from './index.module.scss';

const id = MODAL_IDS.every_5_bonus_group_a;

export const EveryFiveGroupAModal = () => {
  const { modalIds } = useModal();
  const { data: bonusGroup } = useGetBonusGroup();
  return modalIds[id] && bonusGroup && <Modal data={bonusGroup} />;
};

export const Modal = ({ data }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { closeModal } = useModal();
  const {
    bonus_amount,
    investment_amount,
    promotion_for,
    expiration_days,
    fundraising_end_date,
    start_date,
    end_date,
  } = data;

  const { data: round } = useGetFundraisingDates();

  const content = (
    <div className={s.modal}>
      <div className={s.header}>
        <h1 className={s.title}>
          {t('modal:bonus_banner_modal.maximize_your_earning', {
            amountBonus: prettyFloatMoney(bonus_amount),
            amountInvestment: prettyFloatMoney(investment_amount),
          })}
        </h1>
      </div>
      <div className={s.content}>
        <div className={s.content_inner}>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.how_it_work')}
            </div>
            <div>
              <Trans
                ns="modal"
                i18nKey="modal:bonus_banner_modal.every_invested_amount"
                values={{
                  investmentAmount: prettyFloatMoney(investment_amount),
                  startDate: prettyDate(
                    round?.start_date,
                    DATE_FORMAT_MMMM_YYYY,
                  ),
                  bonusAmount: prettyFloatMoney(bonus_amount),
                }}
                components={{ span: <span className="f-500" /> }}
              />
            </div>
            <ul className={s.list}>
              <li>
                <Trans
                  ns="modal"
                  i18nKey="modal:bonus_banner_modal.investment_less_than"
                  values={{
                    investmentAmount: prettyFloatMoney(investment_amount),
                  }}
                  components={{ span: <span className="f-500" /> }}
                />
              </li>
              <li>
                <Trans
                  ns="modal"
                  i18nKey="modal:bonus_banner_modal.investment_over_amount"
                  components={{ span: <span className="f-500" /> }}
                  values={{
                    investmentAmount: prettyFloatMoney(investment_amount),
                    bonusAmount: prettyFloatMoney(bonus_amount),
                  }}
                />
              </li>
              <li>
                <Trans
                  ns="modal"
                  i18nKey="modal:bonus_banner_modal.investment_over_amount_will_get"
                  components={{ span: <span className="f-500" /> }}
                  values={{
                    investmentAmount: prettyFloatMoney(
                      safeDecimal(investment_amount).mul(2).toNumber(),
                    ),
                    bonusAmount: prettyFloatMoney(
                      safeDecimal(bonus_amount).mul(2).toNumber(),
                    ),
                  }}
                />
              </li>
              <li>
                <Trans
                  ns="modal"
                  i18nKey="modal:bonus_banner_modal.over_x3"
                  components={{ span: <span className="f-500" /> }}
                  values={{
                    investmentAmount: prettyFloatMoney(
                      safeDecimal(investment_amount).mul(3).toNumber(),
                    ),
                    bonusAmount: prettyFloatMoney(
                      safeDecimal(bonus_amount).mul(3).toNumber(),
                    ),
                  }}
                />
              </li>
            </ul>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.who_is_promotion_for')}
            </div>
            <div>{promotion_for}</div>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.when_i_will_get_bonus')}
            </div>
            <div>
              <Trans
                ns="modal"
                i18nKey="modal:bonus_banner_modal.bonuses_will_be_accrued"
                components={{ span: <span className="nowrap" /> }}
                values={{
                  date: moment(fundraising_end_date)
                    .add(1, 'day')
                    .format('MMMM Do, YYYY'),
                }}
              />
            </div>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.can_i_withdraw_my_bonuses')}
            </div>
            <div>{t('modal:bonus_banner_modal.use_acquired_bonuses')}</div>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.for_how_long_can_i_use_them')}
            </div>
            <div>
              {t('modal:bonus_banner_modal.use_bonuses_for_up_to', {
                expirationDays: expiration_days,
              })}
            </div>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t(
                'modal:bonus_banner_modal.what_happens_if_i_invest_my_active_bonus',
              )}
            </div>
            <div>
              {t(
                'modal:bonus_banner_modal.invested_bonuses_do_not_participate',
              )}
            </div>
          </section>
          <section>
            <div className="f-body-md f-500">
              {t('modal:bonus_banner_modal.how_long_last')}
            </div>
            <div>
              <Trans
                ns="modal"
                i18nKey="modal:bonus_banner_modal.this_promotion_start_on"
                values={{
                  startDate: moment(start_date).format('MMMM Do, YYYY'),
                  endDate: moment(end_date).format('MMMM Do, YYYY'),
                }}
                components={{ span: <span className="nowrap" /> }}
              />
            </div>
          </section>
        </div>
      </div>
      <div className={s.footer}>
        <Button
          theme="neutral"
          appearance="filled"
          size="lg"
          label={t('modal:bonus_banner_modal.got_it')}
          onClick={() => closeModal(id)}
        />
      </div>
    </div>
  );

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};